import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useAccount } from 'wagmi';
import Web3 from 'web3';
import BigNumber from 'bignumber.js';
import { useFetch } from 'use-http';

import { Vault, VaultsConfig } from '../vaults-config';
import { REACT_APP_OZK_STAKING_TOKEN } from '../../../Config/Config';
import ozkTokenAbi from '../../../ContractABI/OPENZKToken.json';
import yieldFarmAbi from '../../../ContractABI/Yield.json';
import stakingAbi from '../../../ContractABI/Staking.json';
import { useUserContext } from '../../../context/user.context';
import { getConnections, waitForTransactionReceipt } from '@wagmi/core';
import { wagmiAdapterConfig } from '../../../Config/WagmiConfig';
import {
  getRelativeTimeFromNow,
  isCurrentTimeAfter,
} from '../../../utils/date';
import { axiosInstance } from '../../../Helper/Api/axios.instance';
import { AppConfig } from '../../../Config/app-config';

export enum TxStatus {
  WAITING = 'waiting',
  LOADING = 'loading',
  COMPLETED = 'completed',
  ERROR = 'error',
  FAILED_TO_INITIATE = 'failedToInitiate',
}

type UnstakeType = {
  id: number;
  userId: number;
  blockTimestamp: string;
  claimableAt: string;
  txHash: string;
  blockNumber: number;
  amount: number;
  nonce: number;
  stakingAddress: string;
  processed: boolean;
};

interface StakingContextType {
  vaults: Vault[];
  unstakeList: UnstakeType[];
  ozkBalance: number;
  txInProgress: boolean;
  txStatus: TxStatus;
  errorMessage?: string;
  setTxStatus: (status: TxStatus) => void;
  txHash: string;
  selectedTab: string;
  setTxHash: (hash: string) => void;
  setSelectedTab: (selectedTab: string) => void;
  setTxInProgress: (value: boolean) => void;
  isOzkBalanceGraterOrEqThan: (amount: number) => boolean;
  fetchWithdrawList: () => void;
  updateVaults: () => void;
  fetchOzkBalance: () => Promise<void>;
  deposit: (amount: string, vault: Vault) => Promise<void>;
  checkIn: (vault: Vault) => Promise<void>;
  handleUnstake: (vault: Vault) => Promise<void>;
  harvest: (yieldAddress: string, stakingAddress: string) => Promise<void>;
  handleFinalizeUnstake: (
    stakingAddress: string,
    nonce: number,
    id: number,
  ) => Promise<void>;
}

const defaultValue: StakingContextType = {
  ozkBalance: 0,
  txInProgress: false,
  txStatus: TxStatus.WAITING,
  setTxStatus: () => {},
  txHash: '',
  selectedTab: 'vaults',
  setTxHash: () => {},
  setSelectedTab: () => {},
  setTxInProgress: () => {},
  fetchWithdrawList: async () => {},
  updateVaults: async () => {},
  vaults: VaultsConfig.vaults,
  unstakeList: [],
  isOzkBalanceGraterOrEqThan: () => false,
  fetchOzkBalance: async () => {},
  deposit: async () => {},
  checkIn: async () => {},
  handleUnstake: async () => {},
  harvest: async () => {},
  handleFinalizeUnstake: async () => {},
};

const StakingContext = createContext<StakingContextType>(defaultValue);

function StakingContextProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useUserContext();
  const { get: getWithdraws } = useFetch();
  const { address } = useAccount();
  const [ozkBalance, setOzkBalance] = useState<number>(0);
  const [txInProgress, setTxInProgress] = useState<boolean>(false);
  const [txHash, setTxHash] = useState<string>('');
  const [txStatus, setStatus] = useState<TxStatus>(TxStatus.WAITING);
  const [vaults, setVaults] = useState<Vault[]>(VaultsConfig.vaults);
  const [unstakeList, setUnstakeList] = useState<UnstakeType[]>([]);
  const [selectedTab, setSelectedTab] = useState('vaults');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const connections = getConnections(wagmiAdapterConfig);

  const walletProvider = useMemo(async () => {
    if (
      !connections ||
      connections.length === 0 ||
      typeof connections[0].connector.getProvider !== 'function'
    ) {
      return null;
    }

    try {
      const provider = await connections[0].connector.getProvider();
      return new Web3(provider);
    } catch (error) {
      console.error('Error getting wallet provider:', error);
      return null;
    }
  }, [connections]);

  const getUserEpochsToClaim = (
    checkInEpochs: number[],
    harvestEpochs: number[],
    currentEpoch: number,
  ) => {
    return checkInEpochs.filter(
      (epoch) => !harvestEpochs.includes(epoch) && epoch !== currentEpoch,
    );
  };

  const fetchData = useCallback(() => {
    void updateVaults();
    void fetchOzkBalance();
    void fetchWithdrawList();
  }, [address, isLoggedIn, connections]);

  const harvest = useCallback(
    async (yieldAddress: string, stakingAddress: string) => {
      if (!isLoggedIn || !address) {
        return;
      }

      const provider = await walletProvider;
      if (!provider) {
        return;
      }

      const yieldFarmContract = new provider.eth.Contract(
        yieldFarmAbi,
        yieldAddress,
      );
      const stakingContract = new provider.eth.Contract(
        stakingAbi,
        stakingAddress,
      );
      const [currentEpoch, userCheckInEpochs, userHarvestEpochs] =
        await Promise.all([
          stakingContract.methods.getCurrentEpoch().call() as unknown as number,
          stakingContract.methods
            .getUserCheckInEpochs(address)
            .call() as unknown as number[],
          yieldFarmContract.methods
            .getUserHarvestEpochs(address)
            .call() as unknown as number[],
        ]);
      const epochsToClaim = getUserEpochsToClaim(
        userCheckInEpochs,
        userHarvestEpochs,
        currentEpoch,
      );

      if (epochsToClaim.length === 0) {
        console.log('No epochs to claim');
        return;
      }

      setTxInProgress(true);
      setStatus(TxStatus.LOADING);
      try {
        const tx = await yieldFarmContract.methods
          .harvest(epochsToClaim)
          .send({ from: address });

        await waitForTransactionReceipt(wagmiAdapterConfig, {
          confirmations: 1,
          hash: tx.transactionHash as `0x${string}`,
        });
        setTxHash(tx.transactionHash);
        setStatus(TxStatus.COMPLETED);
        fetchData();
      } catch (error) {
        setStatus(TxStatus.ERROR);
        console.error('Error harvesting:', error);
      }
    },
    [address, isLoggedIn, connections, txHash, txInProgress, txStatus],
  );

  const checkIn = useCallback(
    async (vault: Vault) => {
      if (!isLoggedIn || !address) {
        return;
      }

      const provider = await walletProvider;
      if (!provider) {
        return;
      }

      const stakingContract = new provider.eth.Contract(
        stakingAbi,
        vault.stakingContract,
      );
      const currentEpoch = (await stakingContract.methods
        .getCurrentEpoch()
        .call()) as unknown as number;

      const hasUserCheckedIn = (await stakingContract.methods
        .hasUserCheckInEpoch(address, currentEpoch)
        .call()) as boolean;

      if (hasUserCheckedIn) {
        console.log('User has already checked in for this epoch');
        return;
      }

      setTxInProgress(true);
      setStatus(TxStatus.LOADING);
      const ozkContract = new provider.eth.Contract(
        ozkTokenAbi,
        REACT_APP_OZK_STAKING_TOKEN,
      );

      try {
        const stakingAmount = (await stakingContract.methods
          .checkInFee()
          .call()) as unknown as number;
        const allowance = await ozkContract.methods
          .allowance(address, vault.stakingContract)
          .call();

        if (BigNumber(stakingAmount).isGreaterThan(Number(allowance))) {
          const approve = await ozkContract.methods
            .approve(vault.stakingContract, stakingAmount)
            .send({ from: address });

          await waitForTransactionReceipt(wagmiAdapterConfig, {
            confirmations: 1,
            hash: approve.transactionHash as `0x${string}`,
          });
        }

        const l2GasLimit = 1500000;
        const tx = await stakingContract.methods
          .checkIn(l2GasLimit)
          .send({ from: address });
        await waitForTransactionReceipt(wagmiAdapterConfig, {
          confirmations: 1,
          hash: tx.transactionHash as `0x${string}`,
        });
        setTxHash(tx.transactionHash);
        setStatus(TxStatus.COMPLETED);
        fetchData();
      } catch (error) {
        setStatus(TxStatus.ERROR);
        console.error('Error checking in: ', error);
      }
    },
    [address, isLoggedIn, connections, txHash, txInProgress, txStatus],
  );

  const handleUnstake = useCallback(
    async (vault: Vault) => {
      if (!isLoggedIn || !address) {
        return;
      }

      const provider = await walletProvider;
      if (!provider) {
        return;
      }

      if (vault.ozkStaked === 0) {
        return;
      }

      const stakingContract = new provider.eth.Contract(
        stakingAbi,
        vault.stakingContract,
      );

      setTxInProgress(true);
      setStatus(TxStatus.LOADING);

      try {
        const tx = await stakingContract.methods
          .queueWithdraw()
          .send({ from: address });
        await waitForTransactionReceipt(wagmiAdapterConfig, {
          confirmations: 1,
          hash: tx.transactionHash as `0x${string}`,
        });
        setTxHash(tx.transactionHash);
        setStatus(TxStatus.COMPLETED);
        fetchData();
      } catch (error) {
        setStatus(TxStatus.ERROR);
        console.error('Error queue withdraw: ', error);
      }
    },
    [address, isLoggedIn, connections, txHash, txInProgress, txStatus],
  );

  const handleFinalizeUnstake = useCallback(
    async (stakingAddress: string, nonce: number, id: number) => {
      if (!isLoggedIn || !address) {
        return;
      }

      const provider = await walletProvider;
      if (!provider) {
        return;
      }

      const stakingContract = new provider.eth.Contract(
        stakingAbi,
        stakingAddress,
      );

      setTxInProgress(true);
      setStatus(TxStatus.LOADING);

      try {
        const tx = await stakingContract.methods
          .processWithdraw(nonce)
          .send({ from: address });
        await waitForTransactionReceipt(wagmiAdapterConfig, {
          confirmations: 1,
          hash: tx.transactionHash as `0x${string}`,
        });
        setTxHash(tx.transactionHash);
        setStatus(TxStatus.COMPLETED);
        await patchWithdrawByNonce(id);
        fetchData();
        await fetchWithdrawList();
      } catch (error) {
        setStatus(TxStatus.ERROR);
        console.error('Error process withdraw: ', error);
      }
    },
    [address, isLoggedIn, connections, txHash, txInProgress, txStatus],
  );

  const updateVaults = useCallback(async () => {
    if (!isLoggedIn || !address) {
      return;
    }

    const provider = await walletProvider;
    if (!provider) {
      return;
    }

    const updatedVaults = await Promise.all(
      VaultsConfig.vaults.map(async (vault) => {
        const yieldFarmContract = new provider.eth.Contract(
          yieldFarmAbi,
          vault.yieldContract,
        );
        const stakingContract = new provider.eth.Contract(
          stakingAbi,
          vault.stakingContract,
        );
        const ozkContract = new provider.eth.Contract(
          ozkTokenAbi,
          REACT_APP_OZK_STAKING_TOKEN,
        );
        const [
          totalAmountPerEpoch,
          epochDuration,
          currentEpoch,
          userOzkAmount,
          stakingOzkAmount,
          userCheckInEpochs,
          userHarvestEpochs,
          currentEpochEndDate,
          userLockTime,
        ] = await Promise.all([
          yieldFarmContract.methods.totalAmountPerEpoch().call(),
          stakingContract.methods.epochDuration().call(),
          stakingContract.methods.getCurrentEpoch().call() as unknown as number,
          stakingContract.methods.balanceOf(address).call(),
          ozkContract.methods
            .balanceOf(vault.stakingContract)
            .call() as unknown as number,
          stakingContract.methods
            .getUserCheckInEpochs(address)
            .call() as unknown as number[],
          yieldFarmContract.methods
            .getUserHarvestEpochs(address)
            .call() as unknown as number[],
          stakingContract.methods
            .getCurrentEpochEndDate()
            .call() as unknown as number,
          stakingContract.methods.lockTime(address).call() as unknown as number,
        ]);

        let ozkEarned = 0;
        let action = 'Stake';

        // console.log(
        //   `${vault.epoch}: userCheckInEpochs ${userCheckInEpochs} and currentEpoch ${currentEpoch}`,
        // );

        if (userCheckInEpochs.length > 0) {
          const claimableEpochs = getUserEpochsToClaim(
            userCheckInEpochs,
            userHarvestEpochs,
            currentEpoch,
          );
          if (claimableEpochs.length > 0) {
            const userOzkEarned = (await yieldFarmContract.methods
              .getUserRewards(address, claimableEpochs)
              .call()) as number;
            ozkEarned = Number(
              BigNumber(userOzkEarned).div(Math.pow(10, 18)).toFixed(2),
            );
          }
        }

        if (BigNumber(Number(userOzkAmount)).gt(0) && currentEpoch > 0) {
          const userHasCheckedInCurrentEpoch = (await stakingContract.methods
            .hasUserCheckInEpoch(address, currentEpoch)
            .call()) as unknown as boolean;
          if (!userHasCheckedInCurrentEpoch) {
            action = 'Check In';
          }
        }

        const currentEpochPoolSize = await yieldFarmContract.methods
          .getPoolSize(currentEpoch)
          .call();
        const amountPerYear = BigNumber(Number(totalAmountPerEpoch))
          .multipliedBy(365 * 24 * 60 * 60)
          .div(Number(epochDuration));

        const stakingOzkAmountBn = BigNumber(stakingOzkAmount);

        // console.log(
        //   `Vault: ${vault.epoch}, Staking OZK Amount: ${stakingOzkAmountBn.toString()}`,
        // );

        const apr = BigNumber(Number(currentEpochPoolSize)).isZero()
          ? stakingOzkAmountBn.isGreaterThan(0)
            ? stakingOzkAmountBn
                .div(Math.pow(10, 18))
                .multipliedBy(0.01)
                .toFixed(2)
            : 'N/A'
          : amountPerYear
              .div(BigNumber(Number(currentEpochPoolSize)).multipliedBy(100))
              .toFixed(2);

        const ozkStaked = BigNumber(Number(userOzkAmount))
          .div(Math.pow(10, 18))
          .toNumber();

        // console.log(
        //   `${vault.stakingContract} user can unstake: ${isTimestampGrater(Number(userLockTime))} lockTime: ${Number(userLockTime)}`,
        // );

        return {
          ...vault,
          apr,
          ozkStaked,
          ozkEarned,
          action,
          endsIn: getRelativeTimeFromNow(Number(currentEpochEndDate)),
          userCanUnstake: isCurrentTimeAfter(Number(userLockTime)),
        };
      }),
    );
    setVaults(updatedVaults);
  }, [isLoggedIn, address, walletProvider]);

  const fetchOzkBalance = useCallback(async () => {
    if (!address) {
      setOzkBalance(0);
      return;
    }
    try {
      const provider = await walletProvider;
      if (!provider) {
        return;
      }

      const ozkContract = new provider.eth.Contract(
        ozkTokenAbi,
        REACT_APP_OZK_STAKING_TOKEN,
      );

      const [balance, decimals] = await Promise.all([
        (await ozkContract.methods.balanceOf(address).call()) as string,
        (await ozkContract.methods.decimals().call()) as string,
      ]);
      setOzkBalance(
        BigNumber(balance)
          .div(Math.pow(10, parseInt(decimals)))
          .toNumber(),
      );
    } catch (error) {
      console.error(error);
    }
  }, [address, walletProvider]);

  const deposit = useCallback(
    async (amount: string, vault: Vault) => {
      if (!address || !isLoggedIn) return;
      const provider = await walletProvider;
      if (!provider) return;

      setTxInProgress(true);
      setStatus(TxStatus.LOADING);
      setErrorMessage("");

      const stakingAmount = Web3.utils.toWei(amount, 'ether');
      const minOzkAmount = 10000;
      const stakingContract = new provider.eth.Contract(
        stakingAbi,
        vault.stakingContract,
      );
      const ozkContract = new provider.eth.Contract(
        ozkTokenAbi,
        REACT_APP_OZK_STAKING_TOKEN,
      );
      const ozkBalance: string = await stakingContract.methods.balanceOf(address).call();
      if (BigInt(ozkBalance) + BigInt(stakingAmount) < BigInt(Web3.utils.toWei(minOzkAmount.toString(), 'ether'))) {
        setErrorMessage(`Total OZK staked must be greater than ${minOzkAmount} OZK`);
        setStatus(TxStatus.FAILED_TO_INITIATE);
        return;
      }

      try {
        const allowance = await ozkContract.methods
          .allowance(address, vault.stakingContract)
          .call();

        if (BigNumber(stakingAmount).isGreaterThan(Number(allowance))) {
          const approve = await ozkContract.methods
            .approve(vault.stakingContract, stakingAmount)
            .send({ from: address });

          await waitForTransactionReceipt(wagmiAdapterConfig, {
            confirmations: 1,
            hash: approve.transactionHash as `0x${string}`,
          });
        }

        const tx = await stakingContract.methods
          .deposit(Web3.utils.toWei(amount, 'ether'))
          .send({ from: address });
        setTxHash(tx.transactionHash);
        setStatus(TxStatus.COMPLETED);
        fetchData();
      } catch (error) {
        setStatus(TxStatus.ERROR);
        console.error('Error staking:', error);
      }
    },
    [
      address,
      isLoggedIn,
      connections,
      walletProvider,
      fetchData,
      txHash,
      txInProgress,
      txStatus,
      errorMessage
    ],
  );

  const isOzkBalanceGraterOrEqThan = useCallback(
    (amount: number) => ozkBalance >= amount,
    [ozkBalance],
  );

  const fetchWithdrawList = useCallback(async () => {
    try {
      const list = await getWithdraws(AppConfig.apiRoutes.listUnstake());

      setUnstakeList(list?.items);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const patchWithdrawByNonce = useCallback(async (nonce: number) => {
    try {
      const url = AppConfig.apiRoutes
        .patchUnstake()
        .replace(':nonce', encodeURIComponent(nonce));
      await axiosInstance.patch(url, { processed: true });
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn && address) {
      void fetchData();
    } else {
      setOzkBalance(0);
      setVaults(VaultsConfig.vaults);
      setUnstakeList([]);
    }
  }, [isLoggedIn, address, connections]);

  const contextValue = useMemo(
    () => ({
      ozkBalance,
      vaults,
      fetchOzkBalance,
      isOzkBalanceGraterOrEqThan,
      deposit,
      checkIn,
      harvest,
      txInProgress,
      setTxInProgress: (value: boolean) => setTxInProgress(value),
      txStatus,
      errorMessage,
      setTxStatus: (status: TxStatus) => setStatus(status),
      txHash,
      setTxHash: (hash: string) => setTxHash(hash),
      unstakeList,
      handleUnstake,
      handleFinalizeUnstake,
      selectedTab,
      setSelectedTab,
      fetchWithdrawList,
      updateVaults,
    }),
    [
      vaults,
      fetchOzkBalance,
      isOzkBalanceGraterOrEqThan,
      ozkBalance,
      deposit,
      selectedTab,
    ],
  );

  return (
    <StakingContext.Provider value={contextValue}>
      {children}
    </StakingContext.Provider>
  );
}

export default StakingContextProvider;

export const useStakingContext = () => useContext(StakingContext);
